// src/mobile_pages/const/Order_view.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  Printer,
  Trash2,
  Edit2,
  FileText,
  User,
  Calendar,
  CheckCircle2,
  Paperclip,
  Activity,
  Clock
} from "lucide-react";

import { getWorkOrderDetail } from "../../api/const";
import { viewApprovalLine } from "../../api/approval";
import { softDeleteDoc } from "../../api/papers";
import { getProjectName } from "../../api/project";

// 기존 컴포넌트 재사용
import TinyMCEpreview from "../../components/const/TinyMCEpreview";
import ApprovalAction from "../../components/approval/ApprovalAction";
import ApprovalLine_view from "../../components/approval/ApprovalLine_view";
import FileList from "../../components/common/papers/FileList";
import PhotoBoardReadonly from "../../components/const/Photo/PhotoBoardReadonly";
import PeopleView from "../../components/common/people/PeopleView";

export default function OrderView() {
  const { workorderId: dns, id: projectId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [approvalLines, setApprovalLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectName, setProjectName] = useState("");

  // 완료보고서 관련 상태
  const [completions, setCompletions] = useState([]);
  const [selectedCompletion, setSelectedCompletion] = useState(null);

  // 섹션 확장 상태
  const [expandedSections, setExpandedSections] = useState({
    info: true,
    approval: true,
    instruction: true,
    attachment: true,
    completion: true,
  });

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. 프로젝트 이름
        const projRes = await getProjectName(projectId);
        setProjectName(projRes.name);

        // 2. 작업지시서 데이터
        const docRes = await getWorkOrderDetail(dns);
        setData(docRes);
        setCompletions(docRes.completion || []);
        if (docRes.completion?.length > 0) {
          setSelectedCompletion(docRes.completion[0]);
        }

        // 3. 결재선
        try {
          const appRes = await viewApprovalLine(dns);
          setApprovalLines(appRes.data.approval_lines || []);
        } catch (e) {
          console.log("결재선 없음 or 오류", e);
        }

      } catch (err) {
        console.error("데이터 로딩 실패:", err);
        alert("데이터를 불러오지 못했습니다.");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dns, projectId, navigate]);

  // 권한 체크
  const isOwner = data?.created_by?.id === currentUser?.id;
  const canEdit = isOwner && (!data?.status || data?.status === "submit");

  // 삭제 핸들러
  const handleDelete = async () => {
    if (!window.confirm("정말 이 문서를 삭제하시겠습니까? (삭제 후 복구 불가)")) return;
    try {
      await softDeleteDoc(dns, true);
      alert("삭제되었습니다.");
      navigate(-1);
    } catch (e) {
      console.error(e);
      alert("삭제 실패");
    }
  };

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("ko-KR");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">로딩 중...</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-24">
        {/* 헤더 */}
        <header className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-gray-500">시공관리 · 작업지시서</p>
            <div className="flex items-center gap-1.5">
              {data.status === 'approved' ? (
                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold">승인됨</span>
              ) : (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">진행중</span>
              )}
            </div>
          </div>
          <h1 className="text-xl font-bold text-slate-900 leading-tight">
            {data.title || "제목 없음"}
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            DNS: {data.dns || dns}
          </p>
        </header>

        <div className="space-y-3">
          {/* 1. 기본 정보 섹션 - 중요도/빈도 재조정 */}
          <Section
            title="기본 정보"
            expanded={expandedSections.info}
            onToggle={() => toggleSection("info")}
            icon={<FileText size={16} className="text-blue-500" />}
          >
            <div className="space-y-4">
              {/* 현장명 - 가장 상단 */}
              <InfoCard
                label="현장명"
                value={data.project_name || projectName || "-"}
                icon={<Activity size={12} />}
                fullWidth
              />

              {/* 일정 정보 - 핵심 */}
              <div className="grid grid-cols-2 gap-3">
                <InfoCard
                  label="시작일"
                  value={data.meta.start_date || "-"}
                  icon={<Calendar size={12} />}
                />
                <InfoCard
                  label="마감일"
                  value={data.meta.end_date || "-"}
                  icon={<Calendar size={12} />}
                />
              </div>

              {/* 시공 담당자 (중요) */}
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-slate-500">시공 담당자</span>
                </div>
                <div className="text-sm">
                  <PeopleView dns={data.dns} placeholder="지정된 담당자 없음" />
                </div>
              </div>

              {/* 검토자 정보 */}
              <div className="grid grid-cols-2 gap-3">
                <InfoCard
                  label="검토자"
                  value={data.approval?.approver_name || "-"}
                  icon={<User size={12} />}
                />
                {/* 처리기간을 보조 정보로 이동 */}
                <InfoCard
                  label="처리기간"
                  value={
                    data.meta.start_date && data.meta.end_date
                      ? `${Math.ceil((new Date(data.meta.end_date) - new Date(data.meta.start_date)) / 86400000)}일`
                      : "-"
                  }
                  icon={<Clock size={12} />}
                />
              </div>
            </div>
          </Section>

          {/* 2. 결재 현황 */}
          <Section
            title="결재 현황"
            expanded={expandedSections.approval}
            onToggle={() => toggleSection("approval")}
            icon={<CheckCircle2 size={16} className="text-purple-500" />}
          >
            <div className="space-y-4">
              <div className="overflow-x-auto pb-2">
                <ApprovalLine_view lines={approvalLines} />
              </div>
              <ApprovalAction approvalLines={approvalLines} />
            </div>
          </Section>

          {/* 3. 지시 사항 - 작성자/작성일 추가 */}
          <Section
            title="지시 사항"
            expanded={expandedSections.instruction}
            onToggle={() => toggleSection("instruction")}
            icon={<FileText size={16} className="text-green-500" />}
          >
            <div className="flex flex-col gap-3">
              <div className="min-h-[100px] text-sm text-slate-700">
                <TinyMCEpreview html={data.meta.instructions || "<p>내용이 없습니다.</p>"} />
              </div>

              {/* 메타 정보 푸터 */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <User size={10} />
                  <span>{data.created_by?.name || "-"}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar size={10} />
                  <span>{formatDate(data.created_at || data.meta.start_date)}</span>
                </div>
              </div>
            </div>
          </Section>

          {/* 4. 첨부 자료 */}
          <Section
            title="첨부 자료"
            expanded={expandedSections.attachment}
            onToggle={() => toggleSection("attachment")}
            icon={<Paperclip size={16} className="text-orange-500" />}
          >
            <FileList dns={dns} />
          </Section>

          {/* 5. 완료 보고서 - 리스트 형태 개선 및 정보 보강 */}
          <Section
            title={`완료 보고서 (${completions.length})`}
            expanded={expandedSections.completion}
            onToggle={() => toggleSection("completion")}
            icon={<CheckCircle2 size={16} className="text-indigo-500" />}
          >
            {completions.length > 0 ? (
              <div className="space-y-6">
                {/* 완료보고서가 여러 개일 경우 선택/탭 보다는 순차적으로 보여주거나 선택 UI 개선 */}
                {/* 여기서는 드롭다운으로 선택하여 하나씩 상세히 보여주는 방식 유지하되 메타정보 강화 */}

                {completions.length > 1 && (
                  <select
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                    value={selectedCompletion?.dns}
                    onChange={(e) => {
                      const found = completions.find(c => c.dns === e.target.value);
                      setSelectedCompletion(found);
                    }}
                  >
                    {completions.map((c, idx) => (
                      <option key={c.dns} value={c.dns}>
                        [{idx + 1}] {c.title} ({formatDate(c.created_at)})
                      </option>
                    ))}
                  </select>
                )}

                {selectedCompletion && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    {/* 완료보고서 헤더 */}
                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-bold text-slate-800 line-clamp-1">
                          {selectedCompletion.title}
                        </h4>
                        {/* 승인 상태 배지 */}
                        {selectedCompletion.approval?.is_approved === 'approved' ? (
                          <span className="flex-shrink-0 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full">승인</span>
                        ) : selectedCompletion.approval?.is_approved === 'rejected' ? (
                          <span className="flex-shrink-0 px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded-full">반려</span>
                        ) : (
                          <span className="flex-shrink-0 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-bold rounded-full">승인대기</span>
                        )}
                      </div>

                      {/* 메타 정보 그리드 */}
                      <div className="grid grid-cols-2 gap-y-1 gap-x-2 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <span className="text-slate-400">작성자:</span>
                          <span className="font-medium text-slate-700">{selectedCompletion.created_by?.name || "-"}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-slate-400">작성일:</span>
                          <span className="font-medium text-slate-700">{formatDate(selectedCompletion.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-1 col-span-2">
                          <span className="text-slate-400">검토자:</span>
                          <span className="font-medium text-slate-700">{selectedCompletion.approval?.approver_name || "-"}</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 space-y-5">
                      {/* 조치 내용 */}
                      <div>
                        <p className="text-xs font-bold text-slate-800 mb-2 flex items-center gap-1">
                          <FileText size={12} className="text-blue-500" /> 조치 내용
                        </p>
                        <div className="bg-slate-50 rounded-lg p-3 text-sm min-h-[60px] border border-slate-100">
                          <TinyMCEpreview html={selectedCompletion.meta.detail || "<p>내용 없음</p>"} />
                        </div>
                      </div>

                      {/* 사진 대지 - 조치내용의 근거자료 개념으로 포함 */}
                      <div>
                        <p className="text-xs font-bold text-slate-800 mb-2 flex items-center gap-1">
                          <Paperclip size={12} className="text-orange-500" /> 사진 대지
                        </p>
                        {selectedCompletion.meta.photoboard ? (
                          <div className="rounded-lg border border-slate-100 overflow-hidden">
                            <PhotoBoardReadonly photoboardId={selectedCompletion.meta.photoboard} />
                          </div>
                        ) : (
                          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-400 text-center">
                            연결된 사진대지가 없습니다.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-400 py-4 text-center">
                등록된 완료 보고서가 없습니다.
              </p>
            )}
          </Section>

        </div>
      </main>

      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <div className="flex items-center gap-2">
          {/* 목록 버튼 (항상 보임) */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all flex items-center justify-center gap-1"
          >
            <ChevronLeft size={16} />
            목록
          </button>

          {/* 인쇄 버튼 */}
          <button
            type="button"
            onClick={() => window.print()}
            className="w-11 h-11 flex-shrink-0 rounded-full border border-gray-300 bg-white text-gray-700 flex items-center justify-center hover:bg-gray-50 active:scale-[0.97]"
          >
            <Printer size={18} />
          </button>

          {/* 수정/삭제 (권한 있을 때만) */}
          {canEdit && (
            <>
              <button
                type="button"
                onClick={handleDelete}
                className="w-11 h-11 flex-shrink-0 rounded-full border border-red-200 bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 active:scale-[0.97]"
              >
                <Trash2 size={18} />
              </button>
              <button
                type="button"
                onClick={() => {
                  const editUrl = window.location.pathname.replace(/\/view$/, "");
                  navigate(editUrl);
                }}
                className="flex-1 h-11 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 active:scale-[0.97] transition-all flex items-center justify-center gap-1"
              >
                <Edit2 size={16} />
                수정
              </button>
            </>
          )}
        </div>
      </footer>
    </div>
  );
}

// UI 컴포넌트들
function Section({ title, expanded, onToggle, icon, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""
            }`}
        />
      </button>
      {expanded && <div className="p-4">{children}</div>}
    </section>
  );
}

function InfoCard({ label, value, icon, fullWidth = false }) {
  return (
    <div className={`bg-slate-50 rounded-xl p-3 border border-slate-100 ${fullWidth ? 'col-span-2' : ''}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-slate-400">{icon}</span>
        <span className="text-[10px] text-slate-400 font-medium">{label}</span>
      </div>
      <p className="text-[13px] font-semibold text-slate-700 truncate">
        {value}
      </p>
    </div>
  );
}
